import { useState, useEffect, useCallback } from 'react';
import { shouldUseNativePicker, generateThumbnail, isImageFile } from '../../../Utils/nativeFileUpload';

const getNameFromFileUrl = (fileUrl) => {
    if (!fileUrl || typeof fileUrl !== 'string') return null;

    try {
        const pathname = new URL(fileUrl).pathname;
        const encodedName = pathname.split('/').filter(Boolean).pop();
        if (!encodedName) return null;

        const decodedName = decodeURIComponent(encodedName);
        const storagePrefixMatch = decodedName.match(/^[a-f0-9]{32}_(.+)$/i);
        return storagePrefixMatch?.[1] || decodedName;
    } catch (e) {
        const cleanPath = fileUrl.split('?')[0];
        const encodedName = cleanPath.split('/').filter(Boolean).pop();
        return encodedName ? decodeURIComponent(encodedName) : null;
    }
};

/**
 * Manages draft attachments for a conversation.
 * Loads and transforms file_uploads from the conversation into Ant Design fileList format.
 * 
 * @param {Object} selectedConversation - The currently selected conversation
 * @returns {Object} { draftAttachments, setDraftAttachments, clearDrafts }
 */
const useDraftAttachments = (selectedConversation) => {
    const [draftAttachments, setDraftAttachments] = useState([]);

    // Load draft attachments from conversation.file_uploads
    useEffect(() => {
        const loadDrafts = async () => {
            const drafts = selectedConversation?.file_uploads || [];
            if (drafts.length > 0) {
                const isNative = shouldUseNativePicker();

                const fileListItems = await Promise.all(drafts.map(async (draft) => {
                    const draftName = draft.original_filename || draft.filename || draft.name || getNameFromFileUrl(draft.file_url) || 'file';
                    const isImage = isImageFile({
                        type: draft.mime_type,
                        mime_type: draft.mime_type,
                        name: draftName
                    }, draft.file_url);

                    // For native platforms, generate base64 thumbnail for instant preview
                    // For web, use API's file_url directly (performance optimization)
                    let thumbUrl = null;
                    if (isImage) {
                        if (isNative && draft.file_url) {
                            // Fetch and convert to base64 for native
                            try {
                                const response = await fetch(draft.file_url);
                                const blob = await response.blob();
                                const file = new File([blob], draftName || 'image.jpg', { type: draft.mime_type });
                                thumbUrl = await generateThumbnail(file, draft.file_url);
                            } catch (e) {
                                // Fallback to URL if thumbnail generation fails
                                thumbUrl = draft.file_url;
                            }
                        } else {
                            // Web: use URL directly
                            thumbUrl = draft.file_url;
                        }
                    }

                    return {
                        uid: `-draft-${draft.id}`,
                        name: draftName,
                        status: 'done',
                        url: isImage ? draft.file_url : null,
                        thumbUrl: thumbUrl,
                        fileId: draft.id,
                        fileData: draft,
                        size: draft.size,
                        type: draft.mime_type,
                        isImage
                    };
                }));
                setDraftAttachments(fileListItems);
            } else {
                setDraftAttachments([]);
            }
        };

        loadDrafts();
    }, [selectedConversation]);

    const clearDrafts = useCallback(() => {
        setDraftAttachments([]);
    }, []);

    return {
        draftAttachments,
        setDraftAttachments,
        clearDrafts
    };
};

export default useDraftAttachments;
